import { debounce } from 'lodash'
import { removeDuplicatesArray, includesSearch } from '../../utils'
import { handlerUseStateVmodel } from '../mixins'

export const base = () => ({
  mixins: [handlerUseStateVmodel(true)],
  props: {
    allowSelectAll: {'default': true},
    allowUnselectAll: {'default': false},
    filterText: {required: true},
    callback: {required: false},
    disabled: {'default': false, type: Boolean},
    onChange: {type: Function},
    uniqueKey: {'default': 'id'}  // the default field of the object that the component will use to identify each item
  },
  async created () {
    this._loadItems(this.currentPage, '')
  },
  data () {
    return {
      popupVisible: false,
      searching: false
    }
  },
  computed: {
    popUpMinWidth () { return this.$refs.selectbox.clientWidth + 'px' },
    displayedData () { return this.state.loaded },
    loading () { return this.state.loading },
    selectVisible: {
      get () { return this.state.selectVisible },
      async set (selected) {
        if (this.loading && this.popupVisible) { return }

        // here we commit all visible to be selected to not have delay in view while api is loading
        this.state = {selected: this.displayedData, loading: true}
        let updateState = {selectVisible: selected, loading: false}

        if (selected) {
          let toSelectOrDeselect = await this.callback({search: this.search})
          if (this.uniqueKey !== 'id') {
            updateState.loaded = this.parseUniqueKey(toSelectOrDeselect.body.success)
          } else {
            updateState.loaded = toSelectOrDeselect.body ? toSelectOrDeselect.body.success : []
          }
          updateState.selected = removeDuplicatesArray([...this.state.selected, ...updateState.loaded], this.uniqueKey)
        } else {
          const idsToRemove = this.state.loaded.map(i => i[this.uniqueKey])
          updateState.selected = this.state.selected.filter(i => !idsToRemove.includes(i[this.uniqueKey]))
        }

        this.state = updateState
      }
    }
  },
  methods: {
    showPopup () {
      this.popupVisible = true
    },
    hidePopup () {
      if (!this.popupVisible) { return }
      this.searching = false
      this.search = null
      this.popupVisible = false
    },
    onSelect (item) {
      this.state = {selected: [...this.state.selected, item]}
    },
    selectAll () {
      this.selectVisible = !this.selectVisible
      if (this.onChange) {
        this.onChange('selectedAll')
      }
    },
    loadItems: debounce(async function (currentPage, search) {
      // If we use this component multiple times at once the debounce get the cache of vue
      // and only the last component will load for the first time when created
      this._loadItems(currentPage, search)
    }, 100),
    async _loadItems (currentPage, search) {
      // if (this.loading) { return }
      if (!this.callback) { return }

      this.state = {loading: true, selectVisible: false}

      let result = await this.callback({page: currentPage, search})
      if (!result.body) {
        // NO CONTENT
        this.state = {loading: false, loaded: []}
        return
      }

      if (this.uniqueKey !== 'id') {
        result.body.success = this.parseUniqueKey(result.body.success)
      }

      let parsedState = { loading: false }

      if (currentPage !== undefined && !search) {
        parsedState = {
          loading: false,
          ...parsedState,
          loaded: removeDuplicatesArray([...this.displayedData, ...result.body.success], this.uniqueKey),
          currentPage: currentPage,
          totalPages: result.body.totalPages || 1
        }
      } else {
        parsedState = {
          loading: false,
          ...parsedState,
          loaded: result.body.success
        }
      }
      this.state = parsedState
    },
    loadMore () {
      if ((!this.totalPages || this.currentPage + 1 < this.totalPages) && !this.loading && this.showPopup) {
        this.loadItems(this.currentPage + 1, this.search)
      }
    },
    customOnChange (item) {
      this.searching = false
      if (this.onChange) {
        this.onChange(item)
      }
    },
    onSelectWithEnter (item) {
      this.customOnChange(item)
      let selected = false
      const allSelected = this.state.selected
      for (let i in allSelected) {
        let current = allSelected[i]
        if (item.id === current.id) {
          selected = true
        }
      }
      if (selected) {
        this.state = {selected: allSelected.filter(i => i.id !== item[this.uniqueKey])}
      } else {
        this.state = {selected: [...allSelected, item]}
      }
    },
    parseUniqueKey (data) {
      // If the back respond items only as a list, e.g: ['Local A', 'Local b'] we use item itselt as name and id
      // otherwise we will assume that is a list of objects and the coder want a custom unique key
      if (data && data[0][this.uniqueKey] === undefined) {
        data = data.map(item => { return {name: item, id: item} })
      } else {
        data = data.map(item => { return {name: item.name, id: item[this.uniqueKey]} })
      }
      return data
    },
    focusInput () {
      this.$refs.searchInput.focus()
      this.showPopup()
    },
    onFocusInputSeach (e) {
      e.target.select()
      this.showPopup()
    },
    unSelectAll () {
      this.state = {selected: []}
    }
  }
})

export const persistent = () => {
  return {
    mixins: [base()],
    props: {
      // searchKeys has some cool things
      // to only match when length matches too add __len in the string (e.g: for emp code)
      searchKeys: {'default': ['name'], type: Array},
      searchInFront: {'default': true}
    },
    computed: {
      displayedData () {
        if (!this.search || !this.searchInFront) { return this.state.loaded }

        let searchItems = [this.search]

        if (this.search.includes(',')) {
          searchItems = this.search.split(',').filter(i => i !== '')
        } else if (this.search.includes(';')) {
          searchItems = this.search.split(';').filter(i => i !== '')
        }

        return this.state.loaded.filter(item => {
          for (let s in searchItems) {
            const currentSearched = searchItems[s].trim()

            for (let k in this.searchKeys) {
              const currentFieldSearch = this.searchKeys[k].split('__')

              const fieldName = currentFieldSearch[0]
              const onlyMatchLength = currentFieldSearch.includes('len')

              if (!item[fieldName]) { continue }

              let valid = false

              if (includesSearch(item[fieldName], currentSearched)) {
                valid = true
              }
              if (onlyMatchLength) {
                valid = valid && item[fieldName].length === currentSearched.length
              }

              if (valid) { return true }
            }
          }
        })
      },
      selectVisible: {
        get () { return this.state.selectVisible },
        async set (selected) {
          if (this.loading && this.popupVisible) { return }

          // here we commit all visible to be selected to not have daley in view while api is loading
          this.state = {selected: this.displayedData, loading: true}
          let updateState = {selectVisible: selected, loading: false}

          if (selected) {
            updateState.selected = removeDuplicatesArray([...this.state.selected, ...this.displayedData], this.uniqueKey)
          } else {
            const idsToRemove = this.state.loaded.map(i => i[this.uniqueKey])
            updateState.selected = this.state.selected.filter(i => !idsToRemove.includes(i[this.uniqueKey]))
          }

          this.state = updateState
        }
      }
    },
    methods: {
      loadMore () {},
      loadItems () {},
      async _loadItems () {
        this.state = {loading: true, selectVisible: false}

        let params = {}

        if (!this.searchInFront) {
          params = {search: this.state.search}
        }

        let result = await this.callback(params)
        if (!result.body) {
          // NO CONTENT
          this.state = {loading: false, loaded: []}
          return
        }

        if (this.uniqueKey !== 'id') {
          result.body.success = this.parseUniqueKey(result.body.success)
        }

        this.state = {loading: false, loaded: result.body.success}
      }
    }
  }
}
